/**
 * Вспомогательные функции для работы с несколькими store.
 */
import { useWallsStore } from "../layers/wallsStore";
import { useFixturesStore } from "../layers/fixturesStore";
import { useFurnitureFixturesStore, usePlumbingStore } from "../layers/plumbingStore";
import { useFloorItemsStore, usePlumbingItemsStore } from "../layers/floorItemsStore";
import { useFloorsStore } from "../layers/floorsStore";
import { useWaterproofStore } from "../layers/waterproofStore";
import { useScreedStore } from "../layers/screedsStore";
import { useWiringStore } from "../layers/wiringStore";
import { usePipesStore } from "../layers/pipesStore";
import { useCeilingStore } from "../layers/ceilingStore";
import { useBaseboardStore } from "../layers/baseboardStore";
import { useRoomNamesStore } from "../layers/roomNamesStore";
import { useWallFinishStore } from "../layers/wallFinishStore";
import { useCommentsStore } from "../layers/commentsStore";
import { useUnderlayStore } from "../layers/underlayStore";
import { EMPTY_UNDERLAY, LayerKind, type PlanLayers } from "@/shared/plan";

/**
 * Собрать снимок всех слоёв из всех store.
 */
export const getAllLayers = (): PlanLayers => {
	const wallsState = useWallsStore.getState();
	const fixturesState = useFixturesStore.getState();
	const plumbingState = usePlumbingStore.getState();
	const floorItemsState = useFloorItemsStore.getState();
	const plumbingItemsState = usePlumbingItemsStore.getState();
	const floorsState = useFloorsStore.getState();
	const waterproofState = useWaterproofStore.getState();
	const screedState = useScreedStore.getState();
	const wiringState = useWiringStore.getState();
	const pipesState = usePipesStore.getState();

	return {
		walls: {
			kind: LayerKind.Walls,
			visible: wallsState.visible,
			nodes: wallsState.items.nodes,
			walls: wallsState.items.walls,
			openings: wallsState.items.openings,
		},
		electrical: {
			kind: LayerKind.Electrical,
			fixtures: fixturesState.items,
			visible: fixturesState.visible,
			items: fixturesState.ceilingItems,
		},
		plumbing: {
			kind: LayerKind.Plumbing,
			fixtures: plumbingState.items,
			visible: plumbingState.visible,
			items: plumbingItemsState.items,
		},
		furniture: {
			kind: LayerKind.Furniture,
			fixtures: useFurnitureFixturesStore.getState().items,
			visible: floorItemsState.visible,
			items: floorItemsState.items,
		},
		floors: {
			kind: LayerKind.Floors,
			items: floorsState.items,
			visible: floorsState.visible,
		},
		waterproof: {
			kind: LayerKind.Waterproof,
			items: waterproofState.items,
			visible: waterproofState.visible,
		},
		screed: {
			kind: LayerKind.Screed,
			items: screedState.items,
			visible: screedState.visible,
		},
		ceiling: {
			kind: LayerKind.Ceiling,
			items: useCeilingStore.getState().items,
			visible: useCeilingStore.getState().visible,
		},
		baseboard: {
			kind: LayerKind.Baseboard,
			items: useBaseboardStore.getState().items,
			visible: useBaseboardStore.getState().visible,
		},
		rooms: {
			kind: LayerKind.Rooms,
			visible: true,
			items: useRoomNamesStore.getState().items,
		},
		wiring: {
			kind: LayerKind.Wiring,
			items: wiringState.items,
			connections: wiringState.connections,
			groups: wiringState.groups,
			visible: wiringState.visible,
		},
		pipes: {
			kind: LayerKind.Pipes,
			items: pipesState.items,
			visible: pipesState.visible,
		},
		finishes: {
			kind: LayerKind.WallFinish,
			items: useWallFinishStore.getState().items,
			visible: useWallFinishStore.getState().visible,
		},
		comments: {
			kind: LayerKind.Comments,
			items: useCommentsStore.getState().items,
			visible: useCommentsStore.getState().visible,
		},
		underlay: underlayLayer(),
	};
};

/** Слой подложки из её стора. */
const underlayLayer = (): PlanLayers["underlay"] => {
	const { visible, image, x, y, mmPerPixel, opacity } = useUnderlayStore.getState();
	return { kind: LayerKind.Underlay, visible, image, x, y, mmPerPixel, opacity };
};

/**
 * Поменялось ли что-то в слоях между двумя снимками `getAllLayers`. Сторы слоёв при правке
 * заменяют свои объекты, а нетронутые оставляют как есть — поэтому хватает сравнить поля
 * каждого слоя по ссылке, без обхода всего чертежа.
 */
export const layersChanged = (before: PlanLayers, after: PlanLayers): boolean =>
	(Object.keys(before) as (keyof PlanLayers)[]).some((kind) => {
		const a = before[kind] as unknown as Record<string, unknown>;
		const b = after[kind] as unknown as Record<string, unknown>;
		return Object.keys(a).some((field) => a[field] !== b[field]);
	});

export const restoreAllLayers = (layers: PlanLayers) => {
	useWallsStore.getState().restoreWalls({
		nodes: layers.walls.nodes,
		walls: layers.walls.walls,
		openings: layers.walls.openings,
	});
	useFixturesStore.getState().restoreFixtures(layers.electrical.fixtures);
	useFixturesStore.getState().restoreCeilingItems(layers.electrical.items ?? {});
	usePlumbingStore.getState().restoreFixtures(layers.plumbing.fixtures);
	useFloorItemsStore.getState().restoreFloorItems(layers.furniture.items);
	useFurnitureFixturesStore.getState().restoreFixtures(layers.furniture.fixtures ?? {});
	usePlumbingItemsStore.getState().restoreFloorItems(layers.plumbing.items ?? {});
	useFloorsStore.getState().restoreFloors(layers.floors.items);
	useWaterproofStore.getState().restoreWaterproofs(layers.waterproof.items);
	useScreedStore.getState().restoreScreed(layers.screed.items);
	// В старых файлах слой потолков был без данных
	useCeilingStore.getState().restoreCeilings(layers.ceiling?.items ?? {});
	useBaseboardStore.getState().restoreBaseboards(layers.baseboard?.items ?? {});
	// В старых файлах названий комнат нет
	useRoomNamesStore.getState().restoreRoomNames(layers.rooms?.items ?? {});
	useWiringStore.getState().restoreCables(layers.wiring.items);
	useWiringStore.getState().restoreLightingConnections(layers.wiring.connections ?? {});
	useWiringStore.getState().restoreGroups(layers.wiring.groups ?? {});
	// В старых файлах слоя труб может не быть
	usePipesStore.getState().restorePipes(layers.pipes?.items ?? {});
	// В старых файлах слоя отделки нет
	useWallFinishStore.getState().restoreFinishes(layers.finishes?.items ?? {});
	// В старых файлах слоя комментариев нет
	useCommentsStore.getState().restoreComments(layers.comments?.items ?? {});
	// В старых файлах подложки нет
	useUnderlayStore.getState().restoreUnderlay(layers.underlay ?? EMPTY_UNDERLAY);

	// Восстанавливаем видимость
	useFixturesStore.getState().setVisible(layers.electrical.visible);
	usePlumbingStore.getState().setVisible(layers.plumbing.visible);
	useFloorsStore.getState().setVisible(layers.floors.visible);
	useWaterproofStore.getState().setVisible(layers.waterproof.visible);
	useWallsStore.getState().setVisible(layers.walls.visible);
	useScreedStore.getState().setVisible(layers.screed.visible);
	useFloorItemsStore.getState().setVisible(layers.furniture.visible);
	useWiringStore.getState().setVisible(layers.wiring.visible);
	usePipesStore.getState().setVisible(layers.pipes?.visible ?? true);
	// В старых файлах слоя потолков может не быть — тогда потолки выключены
	useCeilingStore.getState().setVisible(layers.ceiling?.visible ?? false);
	useBaseboardStore.getState().setVisible(layers.baseboard?.visible ?? true);
	useWallFinishStore.getState().setVisible(layers.finishes?.visible ?? true);
	useCommentsStore.getState().setVisible(layers.comments?.visible ?? true);
};

/**
 * Временно выставляет видимость слоёв, не меняя данные проекта и историю.
 * Комментарии не трогаем: они идут на все листы экспорта, если пользователь их не скрыл.
 */
export const setVisibleLayers = (visibleLayers: readonly LayerKind[]) => {
	const visible = new Set(visibleLayers);
	useWallsStore.getState().setVisible(visible.has(LayerKind.Walls));
	useFixturesStore.getState().setVisible(visible.has(LayerKind.Electrical));
	usePlumbingStore.getState().setVisible(visible.has(LayerKind.Plumbing));
	useFloorItemsStore.getState().setVisible(visible.has(LayerKind.Furniture));
	useFloorsStore.getState().setVisible(visible.has(LayerKind.Floors));
	useWaterproofStore.getState().setVisible(visible.has(LayerKind.Waterproof));
	useScreedStore.getState().setVisible(visible.has(LayerKind.Screed));
	useWiringStore.getState().setVisible(visible.has(LayerKind.Wiring));
	usePipesStore.getState().setVisible(visible.has(LayerKind.Pipes));
	useCeilingStore.getState().setVisible(visible.has(LayerKind.Ceiling));
	useBaseboardStore.getState().setVisible(visible.has(LayerKind.Baseboard));
	useWallFinishStore.getState().setVisible(visible.has(LayerKind.WallFinish));
};

/** Возвращает только видимость слоёв после временного режима экспорта. */
export const restoreLayerVisibility = (layers: PlanLayers) =>
	setVisibleLayers(
		[
			LayerKind.Walls,
			LayerKind.Electrical,
			LayerKind.Plumbing,
			LayerKind.Furniture,
			LayerKind.Floors,
			LayerKind.Waterproof,
			LayerKind.Screed,
			LayerKind.Ceiling,
			LayerKind.Baseboard,
			LayerKind.Wiring,
			LayerKind.Pipes,
			LayerKind.WallFinish,
		].filter((kind) => {
			switch (kind) {
				case LayerKind.Walls:
					return layers.walls.visible;
				case LayerKind.Electrical:
					return layers.electrical.visible;
				case LayerKind.Plumbing:
					return layers.plumbing.visible;
				case LayerKind.Furniture:
					return layers.furniture.visible;
				case LayerKind.Floors:
					return layers.floors.visible;
				case LayerKind.Waterproof:
					return layers.waterproof.visible;
				case LayerKind.Screed:
					return layers.screed.visible;
				case LayerKind.Ceiling:
					return layers.ceiling.visible;
				case LayerKind.Baseboard:
					return layers.baseboard.visible;
				case LayerKind.Wiring:
					return layers.wiring.visible;
				case LayerKind.Pipes:
					return layers.pipes.visible;
				case LayerKind.WallFinish:
					return layers.finishes.visible;
			}
		}),
	);
